import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/authContext';
import axios from 'axios';
import '../components/HomePage.css';
import { useInView } from 'react-intersection-observer';
import award1 from '../assets/Award1.JPG';
import award2 from '../assets/Award2.JPG';
import award3 from '../assets/Award3.JPG';
import award6 from '../assets/Award6.JPG';
import award5 from '../assets/Award5.jpg';
import award4 from '../assets/Award4.jpg'

const HomePage = () => {
  const { isLoggedIn } = useAuth();
  const [backgroundImage, setBackgroundImage] = useState('');
  const [testimonials, setTestimonials] = useState([]);
  const [newTestimonial, setNewTestimonial] = useState({ name: '', content: '' });
  const [showForm, setShowForm] = useState(false);
  const fileInputRef = useRef(null);
  const navigate = useNavigate();
  const { ref: awardRef, inView: awardsVisible } = useInView({ triggerOnce: true });
  const [topListings, setTopListings] = useState([]);


  useEffect(() => {
    fetchBackgroundImage();
    fetchTestimonials();
  }, []);

  const fetchBackgroundImage = () => {
    axios
      .get('https://amywagnerdesigns.com/api/background-image')
      .then((response) => {
        if (response.data?.image_path) {
          const basePath = 'https://amywagnerdesigns.com/uploads';
          const imageUrl = `${basePath}/${response.data.image_path}`;
          setBackgroundImage(imageUrl);
        }
      })
      .catch((error) => console.error('Error fetching background image:', error));
  };

  useEffect(() => {
    axios.get('https://amywagnerdesigns.com/api/real-estate-listings')
      .then((res) => {
        const listings = res.data.map(listing => ({
          ...listing,
          images: listing.images.map(img => `https://amywagnerdesigns.com/uploads/${img}`)
        }));

        const statusPriority = {
          'for sale': 1,
          'under contract': 2,
          'pending sale': 3,
          'sold': 4
        };

        listings.sort((a, b) => {
          const priorityA = statusPriority[a.status.toLowerCase()] || 99;
          const priorityB = statusPriority[b.status.toLowerCase()] || 99;
          return priorityA === priorityB ? b.id - a.id : priorityA - priorityB;
        });

        setTopListings(listings.slice(0, 3)); // Get top 3 listings
      })
      .catch(err => console.error('Error loading listings:', err));
  }, []);

  const fetchTestimonials = async () => {
    try {
      const response = await axios.get('https://amywagnerdesigns.com/api/testimonials');
      setTestimonials(response.data.testimonials);
    } catch (error) {
      console.error('Error fetching testimonials:', error);
    }
  };

  const handleImageUpload = () => {
    fileInputRef.current.click();
  };

  const handleFileInputChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      const formData = new FormData();
      formData.append('image', file);
      axios
        .post('https://amywagnerdesigns.com/uploadReplaceBackgroundImage', formData)
        .then(() => {
          fetchBackgroundImage();
          alert('Background image updated successfully!');
        })
        .catch((error) => {
          console.error('Error uploading image:', error);
          alert('Failed to update the background image.');
        });
    }
  };

  const handleSubmitTestimonial = async (e) => {
    e.preventDefault();
    try {
      await axios.post('https://amywagnerdesigns.com/api/testimonials', newTestimonial);
      setNewTestimonial({ name: '', content: '' });
      alert('Testimonial submitted for review.');
      setShowForm(false);
    } catch (error) {
      console.error('Error submitting testimonial:', error);
    }
  };

  const handleDeleteTestimonial = async (id) => {
    try {
      await axios.delete(`https://amywagnerdesigns.com/api/testimonials/${id}`);
      setTestimonials(testimonials.filter((testimonial) => testimonial.id !== id));
    } catch (error) {
      console.error('Error deleting testimonial:', error);
    }
  };

  const openFullScreenImage = () => {
    navigate('/fullscreen-background', { state: { backgroundImage } });
  };

  useEffect(() => {
    if (awardsVisible) {
      const canvas = document.getElementById('confetti-canvas');
      const ctx = canvas.getContext('2d');

      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;

      const resizeHandler = () => {
        canvas.width = window.innerWidth;
        canvas.height = window.innerHeight;
      };

      window.addEventListener('resize', resizeHandler);

      const confetti = [];

      function randomColor() {
        const colors = ['#ff0', '#0ff', '#f0f', '#0f0', '#00f', '#f00'];
        return colors[Math.floor(Math.random() * colors.length)];
      }

      function createConfettiPiece() {
        return {
          x: Math.random() * canvas.width,
          y: Math.random() * canvas.height,
          size: Math.random() * 2 + 1/2,
          color: randomColor(),
          speedX: Math.random() * 1 - 1/2,
          speedY: Math.random() * 2 + 1/2
        };
      }

      for (let i = 0; i < 300; i++) {
        confetti.push(createConfettiPiece());
      }

      function draw() {
        ctx.clearRect(0, 0, canvas.width, canvas.height);

        confetti.forEach(c => {
          ctx.fillStyle = c.color;
          ctx.beginPath();
          ctx.arc(c.x, c.y, c.size, 0, Math.PI * 2);
          ctx.fill();
        });
      }

      function update() {
        confetti.forEach(c => {
          c.x += c.speedX;
          c.y += c.speedY;

          if (c.y > canvas.height) {
            c.x = Math.random() * canvas.width;
            c.y = -10;
          }
        });
      }

      let animationFrameId;
      function loop() {
        draw();
        update();
        animationFrameId = requestAnimationFrame(loop);
      }

      loop();

      // Start moving canvas downward after 7 seconds
      const startSlideTimeout = setTimeout(() => {
        canvas.style.transition = 'transform 3s ease-in-out';
        canvas.style.transform = 'translateY(100vh)'; // move canvas out of the screen downwards
      }, 7000);

      // Remove the canvas fully after 10 seconds
      const stopConfettiTimeout = setTimeout(() => {
        cancelAnimationFrame(animationFrameId);
        canvas.remove();
        window.removeEventListener('resize', resizeHandler);
      }, 10000);

      return () => {
        clearTimeout(startSlideTimeout);
        clearTimeout(stopConfettiTimeout);
        cancelAnimationFrame(animationFrameId);
        window.removeEventListener('resize', resizeHandler);
      };
    }
  }, [awardsVisible]);

  return (
    <div>
      {/* Background Image Section */}
      <div className="homepage-container" onClick={openFullScreenImage}>
        <img src={backgroundImage} alt="Background" className="homepage-image" />
        {isLoggedIn && (
          <div>
            <button onClick={handleImageUpload} style={{ color: '#000', padding: '8px', margin: '10px' }}>
              Edit Background Image
            </button>
            <input
              type="file"
              accept="image/*"
              ref={fileInputRef}
              onChange={handleFileInputChange}
              style={{ display: 'none' }}
            />
          </div>
        )}
      </div>

      {/* Schedule Section */}
      <div className="schedule-section">
        <p>Schedule Your Initial Consult Today!</p>
        <Link to="/contact" style={{ textDecoration: 'none', width: '100%' }}>
          <button className="schedule-button">Schedule Today!</button>
        </Link>
      </div>

      {/* Featured Listing */}
      {topListings.length > 0 && (
        <div className="featured-listing-section">
          <h1>Real Estate</h1>

          <div className="featured-wrapper">
            {topListings.map((listing, index) => (
              <Link
                to="/realestateservices" state={{ fromHomepage: true }}
                key={listing.id}
                className={`featured-listing-link ${index === 0 ? 'main' : index === 1 ? 'side-left' : 'side-right'
                  }`}
              >
                <div className="featured-listing-card">
                  <img
                    src={listing.images[0]}
                    alt={`Listing ${index}`}
                    className="featured-listing-image"
                  />
                  <div className="featured-listing-info">
                    <h3>${listing.price}</h3>
                    <p>{listing.address}, {listing.city}, {listing.state} {listing.zip}</p>
                    <p>{listing.beds} Beds, {listing.baths} Baths, {listing.sqft} Sqft</p>
                  </div>
                </div>
              </Link>
            ))}
          </div>

          <h2>Don't miss these listings — your future home may be waiting!</h2>
        </div>
      )}


      {/* Testimonials Section */}
      <div id="testimonials" className="testimonials-section">
        <h2>Testimonials</h2>
        <div className="testimonials-list">
          {testimonials.map((testimonial) => (
            <div key={testimonial.id} className="testimonial">
              <p>"{testimonial.content}"</p>
              <p><strong>- {testimonial.name}</strong></p>
              {isLoggedIn && (
                <button
                  onClick={() => handleDeleteTestimonial(testimonial.id)}
                  style={{
                    color: 'red',
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    marginTop: '10px',
                  }}
                >
                  Delete
                </button>
              )}
            </div>
          ))}
        </div>

        <div className="testimonial-form">
          <button
            onClick={() => setShowForm(!showForm)}
            className="schedule-button small-button"
          >
            Submit Testimonial
          </button>
          {showForm && (
            <form onSubmit={handleSubmitTestimonial}>
              <input
                type="text"
                placeholder="Your Name"
                value={newTestimonial.name}
                onChange={(e) => setNewTestimonial({ ...newTestimonial, name: e.target.value })}
                required
              />
              <textarea
                placeholder="Your Testimonial"
                value={newTestimonial.content}
                onChange={(e) => setNewTestimonial({ ...newTestimonial, content: e.target.value })}
                required
              ></textarea>
              <button type="submit" className="schedule-button small-button">
                Submit
              </button>
            </form>
          )}
        </div>

        {/* Awards Section */}
        <div ref={awardRef} className="awards-section">
          {awardsVisible && (
            <canvas id="confetti-canvas" className="confetti-canvas"></canvas>
          )}

          <h2>Awards & Recognition</h2>
          <div className="award-images">
            <img src={award1} alt="Award 1" />
            <img src={award2} alt="Award 2" />
            <img src={award3} alt="Award 3" />
            <img src={award4} alt="Award 4" />
            <img src={award6} alt="Award 6" />
            <img src={award5} alt="Award 5" />
          </div>
        </div>

        {isLoggedIn && (
          <div className="admin-dashboard-link">
            <Link to="/admin/dashboard" className="admin-dashboard-button">
              Go to Admin Dashboard
            </Link>
          </div>
        )}
      </div>
    </div>
  );
};

export default HomePage;







